
import Image from "./Image";
import { CardLink, useInshurLanding } from "../landingMode";
import { ArrowRight, Truck, BatteryCharging, Disc3, Fuel, KeyRound, LifeBuoy } from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { CardSlideToggle } from "./HomeCardSlideToggle";
import { PricingPennant } from "./PricingPennant";
import { MobilePriceBadge } from "./HomeAiCard";
import { RentShieldCardLogo } from "./RentShieldCardLogo";
/* Roadside Assistance card — a structural twin of the RentShield card (HomeAiCard): same
   rentshield-family gradient, top sheen + edge stroke, logo lockup, word-reveal title,
   subcopy, learn-more and right-side vehicle. The roadside "data" (the six services) lives
   here as a row of glass chips instead of on a separate grid card. "Emergency" is dropped
   from the towing and extraction labels. */
/* Each service also carries a `pos` — where its glass badge sits around the vehicle so the six
   of them ring the convertible like a protective shield — and a `pair`. The two badges sharing
   a `pair` reveal together, and they are deliberately DIAGONALLY OPPOSITE (top-left with
   bottom-right, top-right with bottom-left, then the two mid sides) so each reveal snaps across
   the whole card for a dramatic effect. */
const SERVICES = [
    { icon: Truck, label: "Towing", pos: "left-[27%] top-[28%]", pair: 0 }, // upper-left
    { icon: LifeBuoy, label: "Extraction", pos: "right-[26%] bottom-24", pair: 0 }, // lower-right
    { icon: BatteryCharging, label: "Jump-Start", pos: "right-[24%] top-[26%]", pair: 1 }, // upper-right, just above roof
    { icon: KeyRound, label: "Lockout", pos: "left-10 bottom-8 -translate-y-[52px] sm:left-14", pair: 1 }, // lower-left
    { icon: Disc3, label: "Flat Tire", pos: "left-2 top-1/2 -translate-y-[calc(50%+5px)]", pair: 2 }, // mid-left
    { icon: Fuel, label: "Fuel Delivery", pos: "right-2 top-1/2 -translate-y-1/2", pair: 2 }, // mid-right
];
/* `active` / `onSelect` / `forceReveal` are optional carousel hooks; with no props the card
   renders standalone. The carousel passes them to drive the bottom-left slide toggle and the
   reveal. */
export function HomeRoadsideCard({ active = 1, onSelect, forceReveal = false, cobrand = false, } = {}) {
    const inshur = useInshurLanding(); // /inshur: card is not a link, no "Learn more"
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    const revealed = forceReveal || inView;
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setInView(true);
                observer.disconnect();
            }
        }, { threshold: 0.35 });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    return (<section ref={ref} id="roadside" className="mx-auto w-full max-w-7xl scroll-mt-24 px-5 sm:px-8 md:px-16" aria-labelledby="roadside-heading">
      <CardLink href="https://rentshield.io/roadside-assistance" className={`group relative isolate block overflow-hidden rounded-2xl transition-shadow hover:shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)]${revealed ? " ai-reveal" : ""}`} style={{
            /* Same rentshield-family radial corner-bloom as the RentShield card. */
            backgroundColor: "#050505",
            backgroundImage: "radial-gradient(115% 95% at 10% 12%, #FF5C7A 0%, #C42463 28%, #5E1C48 54%, #1C0B18 80%, #050505 100%)",
        }}>
        {/* Black glass sheen across the top edge. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 z-10 h-40 bg-gradient-to-b from-black/70 via-black/25 to-transparent backdrop-blur-md [mask-image:linear-gradient(to_bottom,black_45%,transparent)]"/>

        {/* Light top-edge stroke. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-30 rounded-2xl border-t border-white/35"/>

        {/* RentShield lockup, pinned top-left (roadside is a RentShield capability). */}
        <RentShieldCardLogo />

        {/* Pennant — top right corner, matching the RentShield insurance card. */}
        <div className="pointer-events-none absolute right-6 -top-px z-20 hidden items-start gap-3 sm:flex">
          <PricingPennant label="Dispatch" price="24/7" unit="coverage" caption="Every trip"/>
        </div>

        <div className="flex flex-col gap-8 px-6 pb-32 pt-6 sm:px-8 sm:pb-28 sm:pt-8 lg:flex-row lg:items-stretch lg:gap-6">
          <div className="flex flex-1 flex-col">
            <div className="mt-auto pt-20 sm:-translate-y-[30px] sm:pt-24 lg:pt-32">
              <span className="mb-3 block font-sans text-xs font-semibold uppercase tracking-[0.16em] text-white/70">
                Roadside Assistance Coverage
              </span>
              <h3 key={active} id="roadside-heading" className="ai-card-title font-sans text-3xl font-normal leading-[1.1] tracking-tight text-white text-balance sm:text-4xl md:text-[42px]">
                {"Roadside assistance built into every trip".split(" ").map((word, i, arr) => (<Fragment key={`${word}-${i}`}>
                    <span className="ai-word" style={{ ["--i"]: i }}>
                      {word}
                    </span>
                    {i < arr.length - 1 ? " " : ""}
                  </Fragment>))}
              </h3>
              {/* Same fixed min-height as the insurance card so both text blocks bottom-anchor
            to an identical top position (eyebrow/title/subcopy line up exactly). */}
              <p className="mt-4 max-w-md font-sans text-base sm:min-h-[7.5rem] sm:text-lg leading-relaxed text-white/60 text-pretty">
                Nationwide 24/7 help on the road for your renters. Built for direct and gig rentals,
                so breakdowns never become your problem or your renter&apos;s.
              </p>
            </div>
          </div>

          {/* Vehicle image — identical to the RentShield card (same source and transform, not
            mirrored). The six roadside services are glass badges ringing the car to show the
            coverage wrapping and protecting it. */}
          {/* Mobile only: pennant as a badge, with the six services stacked beneath it. */}
          <div className="flex flex-col items-start gap-3 sm:hidden">
            <MobilePriceBadge label="Dispatch" price="24/7" unit="coverage"/>
            <ul className="flex flex-wrap gap-2" aria-label="Roadside services">
              {SERVICES.map(({ icon: Icon, label }) => (<li key={label} className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-black/30 px-2.5 py-1 font-sans text-xs font-medium text-white backdrop-blur-md">
                  <Icon aria-hidden="true" strokeWidth={1.75} className="h-3.5 w-3.5 shrink-0"/>
                  {label}
                </li>))}
            </ul>
          </div>

          <div className="relative flex min-h-44 flex-1 items-center justify-center sm:-mt-36 sm:min-h-72 lg:mt-0">
            <Image src="/images/home/card-ai-vehicle.webp" alt="A white SUV lit from behind, wrapped in roadside assistance coverage" width={520} height={520} sizes="(max-width: 1024px) 90vw, 45vw" className="h-auto w-full max-w-md origin-center translate-y-6 scale-[1.35] object-contain mix-blend-lighten sm:translate-y-8"/>

            {/* Six glass protection badges ringing the vehicle. They reveal two at a time
            (grouped by `--pair`) on scroll-in, and re-key on `active` so the pair reveal
            replays whenever the toggle brings this card back into view. */}
            {SERVICES.map(({ icon: Icon, label, pos, pair }) => (<span key={`${label}-${active}`} style={{ ["--pair"]: pair }} className={`ai-card-badge absolute ${pos} z-10 hidden sm:inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-black/40 px-3 py-1.5 font-sans text-xs font-medium text-white shadow-lg backdrop-blur-md sm:gap-2 sm:text-sm`}>
                <Icon aria-hidden="true" strokeWidth={1.75} className="h-4 w-4 shrink-0"/>
                {/* shimmer plays on the label text only */}
                <span className="ai-card-badge__text">{label}</span>
              </span>))}
          </div>
        </div>

        {/* Bottom bar: slide toggle (Insurance / Roadside) on the left, Learn more on the
            right. The toggle only renders inside the carousel (when onSelect is provided). */}
        <div className="absolute inset-x-6 bottom-6 z-20 flex flex-col items-start gap-4 sm:inset-x-8 sm:bottom-8 sm:flex-row sm:items-center sm:justify-between">
          {onSelect ? <CardSlideToggle active={active} onSelect={onSelect}/> : <span />}
          {inshur ? <span /> : (
          <span className="inline-flex items-center gap-2 text-base font-normal text-white sm:text-lg">
            <span className="underline decoration-white/40 underline-offset-4 transition-colors group-hover:decoration-white">
              Learn more
            </span>
            <ArrowRight aria-hidden="true" className="learn-more-arrow h-5 w-5 shrink-0"/>
          </span>
          )}
        </div>
      </CardLink>
    </section>);
}
